import { Button, Grid, Unstable_Grid2 } from '@mui/material';
import Dropdown, { DropdownItem } from 'components/Dropdown/Dropdown';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import ItemReceivedConfirmationModal from 'components/ItemReceivedConfirmationModal/ItemReceivedConfirmationModal';
import BlockImage from 'components/layout/BlockImages/BlockImage/BlockImage';
import Loader from 'components/Loader/Loader';
import { PurchaseFeedbackAction } from 'components/my/purchases/PurchaseFeedbackAction';
import NotFound from 'components/NotFound/NotFound';
import withAuth from 'components/with-auth/with-auth';
import { MY, PRODUCT } from 'config/fe-routes';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IosTextOutline from 'react-ionicons/lib/IosTextOutline';
import OrderService from 'services/order.service';
import { getBundleItemsCount, hasBundleItemsCount, isBundledOrder } from 'utils/bundle-items';
import formatDate from 'utils/format-date';
import { cleanQueryParams, redirectTo } from 'utils/route-helpers';
import { useNotification } from 'utils/snackbar';

import { ProductPriceWithOffer } from '../../../components/ProductPriceWithOffer/ProductPriceWithOffer';

const orderService = new OrderService();

const MyPurchasesPage = () => {
  const { t } = useTranslation();
  const { showWarningNotification } = useNotification();
  const { query } = useRouter();
  const bundledWithPurchaseIds = useRef<number[]>([]);
  const [purchaseToConfirm, setPurchaseToConfirm] = useState(null);
  const [purchaseToRate, setPurchaseToRate] = useState(null);
  const PURCHASES_PER_ROW = 2;
  const PURCHASES_PER_PAGE = PURCHASES_PER_ROW * 10;

  const filters = [
    {
      title: t('pages.purchases.all'),
      value: '',
    },
    {
      title: t('pages.purchases.awaiting'),
      value: 'awaiting',
    },
    {
      title: t('pages.purchases.complete'),
      value: 'complete',
    },
    {
      title: t('pages.purchases.cancelled'),
      value: 'cancelled',
    },
  ];

  const [purchases, setPurchases] = useState([]);
  const [filter, setFilter] = useState<DropdownItem>(filters.find((f) => f.value === query.state) || filters[0]);
  const [hasMorePurchases, setHasMorePurchases] = useState(true);
  const [isLoading, setLoading] = useState(true);

  // Load more purchases
  const loadMorePurchases = async (page: number) => {
    try {
      // Request next set of purchases
      setLoading(true);
      const { list: nextPurchases, total } = await orderService.getOrders(
        cleanQueryParams({
          offset: PURCHASES_PER_PAGE * page,
          limit: PURCHASES_PER_PAGE,
          state: filter?.value || '',
          userType: 'buyer',
        }),
      );

      // eslint-disable-next-line no-unused-expressions
      nextPurchases?.forEach((nextPurchase) => {
        const bundledWith = nextPurchase?.bundled_with || nextPurchase?.bundledWith;

        if (bundledWith) {
          // eslint-disable-next-line no-unused-expressions
          bundledWithPurchaseIds?.current?.push(nextPurchase?.bundled_with);
        }
      });

      // Combine data
      const combinedPurchases = purchases.concat(nextPurchases || []);

      // Check for more
      setHasMorePurchases(!!nextPurchases?.length && total > combinedPurchases.length);

      // Save new purchases to state
      setPurchases(combinedPurchases);
    } catch (e) {
      setHasMorePurchases(false);
    }

    setLoading(false);
  };

  // Confirm purchase
  const confirmPurchase = async (purchase) => {
    try {
      await orderService.updateOrder(purchase.id, { status: 'complete' });
      purchase.status = 'complete'; // Update manually by mutating purchase object
      setPurchases([...purchases]); // Render manual update
    } catch (err) {
      showWarningNotification(t('pages.purchase.error'));
    }
  };

  // Apply filter
  useEffect(() => {
    // Create query params
    const newQueryParams = cleanQueryParams({
      state: filter?.value,
    });

    // Check if query params have changed
    const queryParamsChanged =
      Object.keys(newQueryParams).some((queryParamKey) => newQueryParams[queryParamKey] !== query[queryParamKey]) ||
      Object.keys(query).some((queryParamKey) => query[queryParamKey] !== newQueryParams[queryParamKey]);

    // If query params changed
    if (queryParamsChanged) {
      // Empty cached results
      setPurchases([]);

      // Reset hasMorePurchases flag
      setHasMorePurchases(true);

      redirectTo(
        {
          ...MY.PURCHASES(),
          query: newQueryParams,
        },
        true,
      );
    }
  }, [filter?.value]);

  const purchaseWall = purchases.map((purchase) => {
    const detailsList = [
      {
        label: `${t('pages.purchases.seller')}:`,
        value: purchase.sellerShopName,
      },
      {
        label: `${t('pages.purchases.state')}:`,
        value: t(`pages.purchases.${purchase.status}`),
      },
      {
        label: `${t('pages.purchases.purchase-date')}:`,
        value: formatDate(purchase.createdAt),
      },
      {
        label: `${t('pages.purchases.delivery')}:`,
        value: t([`pages.purchases.${purchase.shipping.originalMethod || purchase.shipping.method}`]),
        bundledWith: purchase.bundled_with || purchase.bundledWith,
        bundledBy: bundledWithPurchaseIds?.current?.includes(purchase.id),
        bundleItemsCount: getBundleItemsCount(purchase),
        status: purchase.status,
      },
      {
        label: `${t('pages.purchases.price')}:`,
        value: <ProductPriceWithOffer order={purchase} />,
      },
    ];

    return (
      <Grid container key={purchase.id} justifyContent="flex-start" direction="column" xs={12} md={6} className="p-2">
        <Grid container className="product-wrapper mb-1">
          {/* Purchase image */}

          <Grid className="product-image">
            <BlockImage
              alt={purchase.product?.images?.[0]?.title}
              src={purchase.product?.images && purchase.product?.images?.[0]?.thumbnail}
              blurhash={purchase.product?.images && purchase.product?.images?.[0]?.blurhash}
              stretch={true}
              {...PRODUCT(purchase.sellerShopName, purchase.product?.slug)}
            />
          </Grid>

          {/* Purchase details */}

          <Grid className="details-wrapper">
            {detailsList.map((li) => (
              <div key={`purchase-label-${li.label}`} className="inline">
                <div className="details left">{li.label}</div>
                <div className="details right">
                  {li.value}

                  {isBundledOrder({
                    status: li.status,
                    bundleItemsCount: li.bundleItemsCount,
                    legacyIsBundled: !!(li.bundledWith || li.bundledBy),
                  }) && (
                    <span className="purchase-bundled">
                      (
                      {hasBundleItemsCount(li.bundleItemsCount)
                        ? t('pages.purchases.order-bundled-count', { count: li.bundleItemsCount })
                        : t('pages.purchases.order-bundled')}
                      )
                    </span>
                  )}
                </div>
              </div>
            ))}
          </Grid>
        </Grid>

        {/* Purchase action buttons */}
        <Grid container className="actions" justifyContent="space-between" gap={1} flexWrap="nowrap">
          {/* More info button */}
          <Grid xs={6} className="side-btn py-0">
            <Button size="small" variant="outlined" color="primary" fullWidth onClick={() => redirectTo(MY.PURCHASE(purchase.id))}>
              {t('common.more-info')}
            </Button>
          </Grid>

          {/* Message button */}
          <Grid xs={6} className="side-btn py-0">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => redirectTo(MY.CONVERSATION(purchase.sellerShopName))}
            >
              <IosTextOutline className="chat-icon" />
            </Button>
          </Grid>
        </Grid>

        <Grid container className="actions" justifyContent="space-between" gap={1} flexWrap="nowrap">
          {/* Confirmation complete button */}
          {purchase?.status === 'in-transit' || purchase?.status === 'paid' ? (
            <Grid xs={12} className="side-btn">
              <Button variant="contained" color="primary" size="medium" fullWidth onClick={() => setPurchaseToConfirm(purchase)}>
                {t('pages.purchase.complete')}
              </Button>
            </Grid>
          ) : null}

          {/* Feedback button / automatic feedback banner */}
          <div style={{ width: '100%' }} className="side-btn">
            <PurchaseFeedbackAction
              canLeaveFeedback={purchase?.canLeaveFeedback}
              feedbackIdForSeller={purchase?.feedbackIdForSeller}
              automaticFeedbackForSeller={purchase?.automaticFeedbackForSeller}
              shopSlug={purchase.sellerShopName}
              onLeaveFeedback={() => setPurchaseToRate(purchase)}
              buttonSize="small"
              className="" // empty string overrides the default mt-2 margin since layout is handled by the parent div
            />
          </div>
        </Grid>
      </Grid>
    );
  });

  return (
    <div className="purchases">
      {/* HEADER */}
      <Header className="purchases-header">
        <HeaderIconLogo icon="home" showLogo={false}>
          {t('pages.purchases.title')}
        </HeaderIconLogo>

        {/* FILTERING */}
        <div className="filtering-row">
          <div className="filtering-title">{t('common.filter')}</div>

          <Dropdown
            items={filters}
            onSelect={(filterOption) => setFilter(filterOption)}
            selectedItem={filter}
            displayEmpty={true}
            modalTitle={t('common.filter')}
            className="filtering-value"
          />
        </div>
      </Header>

      {/* PURCHASES */}
      <InfiniteScroll
        dataLength={purchases.length}
        batchSize={PURCHASES_PER_PAGE}
        loadMore={(page) => loadMorePurchases(page)}
        hasMore={hasMorePurchases}
        loader={<Loader />}
        resetDependencies={[query]}
      >
        <div className="mt-2">
          <Unstable_Grid2 container spacing={4}>
            {purchaseWall}
          </Unstable_Grid2>
        </div>
      </InfiniteScroll>

      <ItemReceivedConfirmationModal
        open={!!(purchaseToConfirm || purchaseToRate)}
        onClose={() => {
          setPurchaseToConfirm(null);
          setPurchaseToRate(null);
        }}
        onConfirm={() => confirmPurchase(purchaseToConfirm)}
        onFeedbackSubmitted={() => {
          const purchase = purchaseToConfirm || purchaseToRate;

          if (purchase) {
            purchase.feedbackIdForSeller = -1;
            purchase.canLeaveFeedback = false;
            setPurchases([...purchases]);
          }
          setPurchaseToConfirm(null);
          setPurchaseToRate(null);
        }}
        shopId={(purchaseToConfirm || purchaseToRate)?.product?.shopId}
        orderId={(purchaseToConfirm || purchaseToRate)?.id}
        sellerName={(purchaseToConfirm || purchaseToRate)?.sellerShopName}
        productImage={(purchaseToConfirm || purchaseToRate)?.product?.images?.[0]?.thumbnail}
        ratingOnly={!!purchaseToRate}
      />

      {!isLoading && purchases?.length === 0 && (
        <Grid container justifyContent="center">
          <NotFound
            title={t('pages.purchases.not-found-title')}
            icon={<img src="/icons/icon-purchases-not-found.svg" alt="" style={{ marginBottom: '10px', marginTop: '40px' }} />}
            target="order"
            customMessage="pages.purchases.not-found"
          />
        </Grid>
      )}
    </div>
  );
};

export default withAuth(MyPurchasesPage);
